/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

// Pending: We should use a library instead of implemeting the flow ourselves. There don't seem to be any up-to-date
//  ones for Vue2, though. Reconsider after migration to Vue3.

import api from "@/config/api"
import Vue from "vue"
import { AUTH_URL, CLIENT_ID, PREMATURE_RENEW_ACCESS_TOKEN_PERIOD } from "@/config/auth"
import { AUTH_HOST } from "@/config/urls"
import { EventBus } from "~/common/src/event-bus"
import store from "@/store"

export { registerInterceptor, login, logout, logoutRemoteDevices, triggerAuthentication, mixin }

export const ACCESS_TOKEN_KEY = "access_token"
let mutex_lock = false

/**
 * Retrieves access token from URL, local storage or triggers authentication
 */
// return Promise that resolves when auth is finished
function login() {
  const code = retrieveCodeFromUrl()
  const state = retrieveStateFromUrl()
  // cleanup URL
  window.history.replaceState({}, document.title, window.location.pathname)

  if (code) {
    return exchangeCodeForToken(code).then((token) => {
      storeTokenInLocalStorage(token)
      finishLogin(token, state)
    })
  }

  const token = retrieveTokenFromStorage()

  if (!token) {
    triggerAuthentication()

    // NEVER resolve – page will reload
    return new Promise(() => {})
  }

  finishLogin(token)
  return Promise.resolve()
}

function finishLogin(accessToken, redirect) {
  store.commit("auth/authSuccess", accessToken)

  store.dispatch("user/loadInfo").then(() => {
    if (redirect) {
      window.location.replace(redirect)
    } else {
      EventBus.$emit("OAuth:login:finished")
    }
  })
}

function registerInterceptor() {
  Vue.http.interceptors.push(function () {
    return function (response) {
      if (response.status === 401 && !mutex_lock) {
        triggerAuthentication()
      }
    }
  })
}

/**
 * Deletes local access token and redirects to global logout
 */
function logout() {
  EventBus.safeTrigger("OAuth:logout")
  let currentToken = store.getters["auth/accessToken"]
  store.commit("auth/authLogout")
  invalidateToken(currentToken).then(
    () => {
      log.info("Access token has been removed successfully - Redirecting to global logout.")
      window.localStorage.removeItem(ACCESS_TOKEN_KEY)
      window.location = AUTH_HOST + "/logout"
    },
    (response) => {
      log.error(
        response.status,
        " Error when trying to delete access token - Redirecting to global logout anyway. ",
        response.body
      )
      window.localStorage.removeItem(ACCESS_TOKEN_KEY)
      window.location = AUTH_HOST + "/logout"
    }
  )
}

/**
 * Performs a remote-logout of other devices authenticated with the same account
 */
function logoutRemoteDevices() {
  let token = JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY))
  Vue.http.delete(api.SESSIONS(), { headers: { Authorization: token.token_type + " " + token.value } }).then(
    () => {
      log.info("Other devices authenticated with this account have been signed out.")
    },
    (error) => {
      log.error("Error when signing out other devices authenticated with this account. ", error)
    }
  )
}

/**
 * A Vue.js mixin that makes sure the 'authRequired' page is displayed during redirection
 */
const mixin = {
  created() {
    if (this.isRedirecting) {
      this.$router.push({ name: "authRequired" })
    }
  },
  computed: {
    isRedirecting() {
      return this.$store.getters["auth/isRedirecting"]
    },
  },
  watch: {
    isRedirecting(val) {
      if (val) {
        this.$router.push({ name: "authRequired" })
      }
    },
  },
}

/**
 * Requests a new access token by triggering an OAuth2 Authentication Code Grant flow
 */
function triggerAuthentication() {
  store.commit("auth/authRedirecting")

  const redirectUrl = window.location.origin + "/control"
  window.location =
    AUTH_URL +
    "?client_id=" +
    CLIENT_ID +
    "&redirect_uri=" +
    encodeURIComponent(redirectUrl) +
    "&response_type=code" +
    "&state=" +
    encodeURIComponent(window.location.href)
}

function retrieveCodeFromUrl() {
  const params = new URLSearchParams(window.location.search)
  const code = params.get("code")

  if (!code) {
    return null
  }

  return code
}

function retrieveStateFromUrl() {
  const params = new URLSearchParams(window.location.search)
  const state = params.get("state")

  if (!state) {
    return null
  }

  return state
}

function exchangeCodeForToken(code) {
  const redirectUrl = window.location.origin + "/control"

  return Vue.http
    .post(
      AUTH_HOST + "/oauth/token",
      new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        redirect_uri: redirectUrl,
        client_id: CLIENT_ID,
      }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    )
    .then((response) => {
      const body = response.body

      return {
        value: body.access_token,
        token_type: body.token_type,
        expiry: new Date(Date.now() + body.expires_in * 1000),
        scope: body.scope,
      }
    })
}

/**
 * Retrieves the access token from local storage if present
 *
 * @returns the access token object (null if not present)
 */
function retrieveTokenFromStorage() {
  let token = JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY))
  if (!(token && token.expiry)) {
    return null
  }

  token.expiry = new Date(token.expiry)
  if (!tokenBeforePrematureRenewPeriod(token)) {
    // token needs to be invalidated and removed from local storage so it can no longer be used
    mutex_lock = true
    log.debug(
      "Access token from local storage has a remaining validity period below the configured PREMATURE_RENEW_ACCESS_TOKEN_PERIOD " +
        "- Invalidating it."
    )
    invalidateToken(token).finally(() => {
      mutex_lock = false
    })
    window.localStorage.removeItem(ACCESS_TOKEN_KEY)
    return null
  }

  return token
}

/**
 * Stores an access token object in local storage
 *
 * @param token
 */
function storeTokenInLocalStorage(token) {
  localStorage.setItem(ACCESS_TOKEN_KEY, JSON.stringify(token))
}

/**
 * Validates whether a token's remaining validity period is larger than the specified {PREMATURE_RENEW_ACCESS_TOKEN_PERIOD}.
 * Invalidates and deletes token if remaining validity period is below the specified value.
 *
 * @param token
 * @returns {boolean}
 */
function tokenBeforePrematureRenewPeriod(token) {
  return token && (token.expiry - Date.now()) / 1000 > PREMATURE_RENEW_ACCESS_TOKEN_PERIOD
}

/**
 * Invalidates given token at the server
 *
 * @param token
 * @returns {Promise<any>}
 */
function invalidateToken(token) {
  return new Promise((resolve, reject) => {
    Vue.http.delete(api.ACCESS_TOKEN(), { headers: { Authorization: token.token_type + " " + token.value } }).then(
      () => {
        log.info("Access token has been invalidated on authorization server")
        resolve()
      },
      (response) => {
        if (response.status === 401) {
          log.info("Access token has already been invalidated or expired. Nothing to do.")
          resolve()
        } else {
          log.error("Error when invalidating access token on authorization server ", response)
          reject(response)
        }
      }
    )
  })
}
